import { loop, Loop } from 'redux-loop';

import { HttpError } from '../utils/HttpError';
import { runLoopFetch } from '../utils/loop';
import {
  CCTCalculationRequest,
  SingleCalculationResultsCCTArbCR,
  JobId
} from '../../types/Calculation';
import auth0Client from 'src/Auth';
import { API_BASE } from 'src/config';

export const SINGLE_CALC_FETCH_START = 'SINGLE_CALC_HACCT_ARBCR_FETCH_START';
export const SINGLE_CALC_FETCH_SUCCESS = 'SINGLE_CALC_HACCT_ARBCR_FETCH_SUCCESS';
export const SINGLE_CALC_FETCH_FAILURE = 'SINGLE_CALC_HACCT_ARBCR_FETCH_FAILURE';
export const SINGLE_CALC_POLL_SUCCESS =
  'SINGLE_CALC_HACCT_ARBCR_POLL_SUCCESS';
export const SINGLE_CALC_POLL_FAILURE =
  'SINGLE_CALC_HACCT_ARBCR_POLL_FAILURE';

type CalculationAction =
  | { type: typeof SINGLE_CALC_FETCH_START; steelValues: CCTCalculationRequest }
  | {
    type: typeof SINGLE_CALC_FETCH_SUCCESS;
    payload: JobId;
  }
  | {
    type: typeof SINGLE_CALC_POLL_SUCCESS;
    payload: SingleCalculationResultsCCTArbCR;
  }
  | { type: typeof SINGLE_CALC_FETCH_FAILURE; payload?: any }
  | { type: typeof SINGLE_CALC_POLL_FAILURE; payload?: any };

export interface CalculationState {
  singleCalcResults: SingleCalculationResultsCCTArbCR | null;
  fetching: boolean;
  error: { status?: number; message: string } | null;
}

const initialState: CalculationState = {
  fetching: false,
  singleCalcResults: null,
  error: null,
};

const fetchSingleCalculation = async (
  steelValues: CCTCalculationRequest
): Promise<JobId> => {
  const res = await fetch(`${API_BASE}/calculation/singleHACCTArbCR`, {
    body: JSON.stringify(steelValues),
    credentials: 'same-origin',
    headers: {
      Authorization: `Bearer ${auth0Client.getIdToken()}`,
      'content-type': 'application/json'
    },
    method: 'POST'
  });

  if (!res.ok) {
    console.log(steelValues);
    console.log(res);
    let message = res.statusText || 'Request failed';
    if (res.status === 502) {
      const hintedTarget = process.env.REACT_APP_BACKEND_URL || '(not set)';
      message = `Bad Gateway: dev proxy could not reach backend. Check REACT_APP_BACKEND_URL=${hintedTarget} or BACKEND_URL and ensure backend is running.`;
    }
    throw new HttpError({ status: res.status, message });
  }

  return res.json();
};


const wait = (ms: number) => new Promise(res => setTimeout(res, ms));

const parseBackendJsonString = (value: string): any => {
  try {
    return JSON.parse(value);
  } catch (_error) {
    throw new Error(value || 'Unexpected CCT result format');
  }
};

const getBackendErrorMessage = (payload: any): string | null => {
  const error = payload && payload.error;

  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error.message === 'string') {
    return error.message;
  }

  return null;
};

export const normalizeCctResult = (
  payload: any
): SingleCalculationResultsCCTArbCR => {
  let normalized = payload;

  if (typeof normalized === 'string') {
    normalized = parseBackendJsonString(normalized);
  }

  const topLevelError = getBackendErrorMessage(normalized);
  if (topLevelError) {
    throw new Error(topLevelError);
  }

  if (typeof normalized?.Result === 'string') {
    normalized = {
      ...normalized,
      Result: parseBackendJsonString(normalized.Result)
    };
  }

  const hasResultWrapper = normalized?.Result !== undefined;
  let result = normalized?.Result;
  if (result?.Result !== undefined) {
    result = result.Result;
  }

  const resultError = getBackendErrorMessage(result);
  if (resultError) {
    throw new Error(resultError);
  }

  if (!hasResultWrapper && normalized?.cooling_curves !== undefined) {
    result = normalized;
  }

  if (!Array.isArray(result?.cooling_curves) || result.cooling_curves.length === 0) {
    throw new Error(
      'Unexpected CCT result format: Result.cooling_curves is missing or empty'
    );
  }

  return hasResultWrapper
    ? {
      ...normalized,
      Result: result
    }
    : {
      Result: result
    };
};

export const fetchSingleResult = async (
  jobid: JobId, count: number
): Promise<SingleCalculationResultsCCTArbCR> => {

  const jid = String((jobid as any)?.id ?? (jobid as any)?.jobid ?? jobid);
  const res = await fetch(`${API_BASE}/calculation/singleHACCTArbCRResult/` + encodeURIComponent(jid) + `?wait=true&maxWaitMs=25000`, {
    credentials: 'same-origin',
    headers: {
      Authorization: `Bearer ${auth0Client.getIdToken()}`,
      'content-type': 'application/json'
    },
    method: 'GET'
  });

  if (!res.ok) {
    let message = res.statusText || 'Request failed';
    if (res.status === 502) {
      const hintedTarget = process.env.REACT_APP_BACKEND_URL || '(not set)';
      message = `Bad Gateway: dev proxy could not reach backend. Check REACT_APP_BACKEND_URL=${hintedTarget} or BACKEND_URL and ensure backend is running.`;
    }
    throw new HttpError({ status: res.status, message });
  }
  if (res.status !== 200) {
    if (count++ < 80) {
      await wait(15000);
      return fetchSingleResult(jobid, count);
    } else {
      throw new Error('max retries reached');
    };
  }

  return normalizeCctResult(await res.json());
};

/**
 * Redux-action creator.
 *
 * @param steelValues the alloy components etc.
 */
export const initFetchSingleCalculation = (steelValues: CCTCalculationRequest) => ({
  steelValues,
  type: SINGLE_CALC_FETCH_START
});

/**
 * Redux-reducer.
 */
export function calculation(
  state: CalculationState = initialState,
  action: CalculationAction
): CalculationState | Loop<CalculationState, any> {
  switch (action.type) {
    case SINGLE_CALC_FETCH_START:
      return loop(
        { ...state, fetching: true, error: null },
        runLoopFetch(
          fetchSingleCalculation,
          SINGLE_CALC_FETCH_SUCCESS,
          SINGLE_CALC_FETCH_FAILURE,
          [action.steelValues]
        )
      );

    case SINGLE_CALC_FETCH_FAILURE:
      return { ...state, fetching: false, singleCalcResults: null, error: { status: (action as any)?.payload?.status, message: (action as any)?.payload?.message || 'Request failed' } };

    case SINGLE_CALC_FETCH_SUCCESS:
      return loop(
        { ...state, fetching: true, error: null },
        runLoopFetch(
          fetchSingleResult,
          SINGLE_CALC_POLL_SUCCESS,
          SINGLE_CALC_POLL_FAILURE,
          [action.payload, 0]
        )
      );

    case SINGLE_CALC_POLL_SUCCESS:
      // What is in ...state? isn't it just fetching and singleCalcResults?
      return { ...state, fetching: false, singleCalcResults: action.payload, error: null };

    case SINGLE_CALC_POLL_FAILURE:
      return { ...state, fetching: false, singleCalcResults: null, error: { status: (action as any)?.payload?.status, message: (action as any)?.payload?.message || 'Request failed' } };

    default:
      return state;
  }
}
